import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  TextField,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import SendIcon from '@mui/icons-material/Send';
import LoginIcon from '@mui/icons-material/Login';

const ReviewDetailDialog = ({
  open,
  onClose,
  selectedThread,
  comments,
  isLoadingComments,
  newComment,
  setNewComment,
  onAddComment,
  onVote,
  getUserVoteStatus,
  isAuthenticated
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Format date to a readable format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleLoginRedirect = () => {
    onClose();
    navigate('/login?activeTab=0');
  };

  if (!selectedThread) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          border: isMobile ? 'none' : '1px solid rgba(255, 0, 0, 0.3)',
          boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)',
          height: isMobile ? '100%' : 'auto',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      TransitionProps={{
        onEnter: () => { if (isMobile) document.body.style.overflow = 'hidden'; },
        onExited: () => { if (isMobile) document.body.style.overflow = 'auto'; }
      }}
    >
      <DialogTitle
        sx={{
          color: 'white',
          pb: 1,
          borderBottom: '1px solid rgba(255, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: isMobile ? 'sticky' : 'relative',
          top: isMobile ? 0 : 'auto',
          zIndex: isMobile ? 1100 : 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.95)'
        }}
      >
        <Box sx={{ overflow: 'hidden', flexGrow: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: { xs: 'nowrap', sm: 'normal' },
              fontSize: { xs: '1.2rem', sm: '1.5rem' }
            }}
          >
            {selectedThread.title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 0, 0, 0.6)',
            borderRadius: '4px'
          },
          '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0, 0, 0, 0.2)' }
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            borderBottom: '1px solid rgba(255, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'flex-start'
          }}
        >
          {selectedThread.threadPoster && (
            <Box
              sx={{
                mr: { xs: 0, sm: 3 },
                ml: { xs: 0, sm: 1 },
                mb: { xs: 3, sm: 0 },
                overflow: 'hidden',
                borderRadius: 1,
                width: { xs: '140px', sm: '200px' },
                height: { xs: '210px', sm: '300px' },
                flexShrink: 0,
                mx: { xs: 'auto', sm: 0 }
              }}
            >
              <Box
                component="img"
                src={selectedThread.threadPoster}
                alt={selectedThread.title}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
                  transition: 'all 0.5s ease',
                  '&:hover': { transform: 'scale(1.05)', filter: 'brightness(1.1)' }
                }}
              />
            </Box>
          )}
          <Box sx={{ flex: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1, textAlign: 'left', mt: { xs: selectedThread.threadPoster ? 0 : 2, sm: 0 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'white' }}>
                {selectedThread.userHandle || 'Anonymous'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                {formatDate(selectedThread.createdAt)}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: 'white', mt: { xs: 2, sm: 1 }, mb: 2, textAlign: 'left', whiteSpace: 'pre-line' }}>
              {selectedThread.content}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'flex-start' }}>
              {isAuthenticated ? (
                <>
                  <IconButton
                    size="small"
                    onClick={() => onVote(selectedThread.threadId, 'thread', 'up')}
                    sx={{ 
                      color: getUserVoteStatus ? 
                        (getUserVoteStatus('thread', selectedThread.threadId) === 'upvote' || getUserVoteStatus('thread', selectedThread.threadId) === 'up' ? 
                          '#ff0000' : 'rgba(255, 255, 255, 0.7)') : 
                        'rgba(255, 255, 255, 0.7)',
                      mr: 0.5 
                    }}
                  >
                    <ThumbUpIcon />
                  </IconButton>
                  <Typography sx={{ color: 'white', mr: 2 }}>
                    {selectedThread.upvotes || 0}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => onVote(selectedThread.threadId, 'thread', 'down')}
                    sx={{ 
                      color: getUserVoteStatus ? 
                        (getUserVoteStatus('thread', selectedThread.threadId) === 'downvote' || getUserVoteStatus('thread', selectedThread.threadId) === 'down' ? 
                          '#ff0000' : 'rgba(255, 255, 255, 0.7)') : 
                        'rgba(255, 255, 255, 0.7)',
                      mr: 0.5 
                    }}
                  >
                    <ThumbDownIcon />
                  </IconButton>
                  <Typography sx={{ color: 'white' }}>
                    {selectedThread.downvotes || 0}
                  </Typography>
                </>
              ) : (
                <>
                  <ThumbUpIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', mr: 0.5, fontSize: '1.2rem' }} />
                  <Typography sx={{ color: 'white', mr: 2 }}>
                    {selectedThread.upvotes || 0}
                  </Typography>
                  <ThumbDownIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', mr: 0.5, fontSize: '1.2rem' }} />
                  <Typography sx={{ color: 'white', mr: 2 }}>
                    {selectedThread.downvotes || 0}
                  </Typography>
                  <Button 
                    variant="outlined"
                    size="small"
                    onClick={handleLoginRedirect}
                    startIcon={<LoginIcon />}
                    sx={{ 
                      ml: 1,
                      color: '#ff0000',
                      borderColor: 'rgba(255, 0, 0, 0.5)',
                      '&:hover': {
                        borderColor: '#ff0000',
                        backgroundColor: 'rgba(255, 0, 0, 0.1)'
                      },
                      textTransform: 'none'
                    }}
                  >
                    Sign in to vote
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ color: 'white', mb: 2, borderBottom: '1px solid rgba(255, 0, 0, 0.2)', pb: 1, textAlign: 'left' }}>
            Discussion
          </Typography>
          {isLoadingComments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: '#ff0000' }} />
            </Box>
          ) : comments.length > 0 ? (
            <List sx={{ width: '100%', p: 0 }}>
              {comments.map((comment) => (
                <ListItem
                  key={comment.commentId || comment.postId}
                  alignItems="flex-start"
                  sx={{ px: { xs: 0.5, sm: 1 }, py: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)', flexDirection: 'row' }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>
                        {comment.userHandle || 'Anonymous'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        {formatDate(comment.createdAt)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                      {comment.content}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {isAuthenticated ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => onVote(comment.commentId || comment.postId, comment.commentId ? 'comment' : 'post', 'up')}
                              sx={{ 
                                color: getUserVoteStatus ? 
                                  (getUserVoteStatus(comment.commentId ? 'comment' : 'post', comment.commentId || comment.postId) === 'upvote' || 
                                   getUserVoteStatus(comment.commentId ? 'comment' : 'post', comment.commentId || comment.postId) === 'up' ? 
                                    '#ff0000' : 'rgba(255, 255, 255, 0.7)') : 
                                  'rgba(255, 255, 255, 0.7)',
                                mr: 0.5 
                              }}
                            >
                              <ThumbUpIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 1.5 }}>
                              {comment.upvotes || 0}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => onVote(comment.commentId || comment.postId, comment.commentId ? 'comment' : 'post', 'down')}
                              sx={{ 
                                color: getUserVoteStatus ? 
                                  (getUserVoteStatus(comment.commentId ? 'comment' : 'post', comment.commentId || comment.postId) === 'downvote' || 
                                   getUserVoteStatus(comment.commentId ? 'comment' : 'post', comment.commentId || comment.postId) === 'down' ? 
                                    '#ff0000' : 'rgba(255, 255, 255, 0.7)') : 
                                  'rgba(255, 255, 255, 0.7)',
                                mr: 0.5 
                              }}
                            >
                              <ThumbDownIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 2 }}>
                              {comment.downvotes || 0}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <ThumbUpIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.5)', mr: 0.5 }} />
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 1.5 }}>
                              {comment.upvotes || 0}
                            </Typography>
                            <ThumbDownIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.5)', mr: 0.5 }} />
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 1.5 }}>
                              {comment.downvotes || 0}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', py: 2 }}>
              No comments yet. Be the first to join the discussion!
            </Typography>
          )}
        </Box>
      </DialogContent>
      <Box
        sx={{
          borderTop: '1px solid rgba(255, 0, 0, 0.3)',
          p: 2,
          position: isMobile ? 'sticky' : 'relative',
          bottom: isMobile ? 0 : 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 1000
        }}
      >
        {isAuthenticated ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#ff0000' }
                },
                '& .MuiOutlinedInput-input': { color: '#fff' }
              }}
            />
            <IconButton onClick={onAddComment} sx={{ color: '#ff0000', alignSelf: 'flex-end' }}>
              <SendIcon />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
              Sign in to join the discussion
            </Typography>
            <Button
              variant="contained"
              onClick={handleLoginRedirect}
              startIcon={<LoginIcon />}
              sx={{
                bgcolor: '#ff0000',
                '&:hover': {
                  bgcolor: '#cc0000'
                },
                textTransform: 'none'
              }}
            >
              Sign In
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default ReviewDetailDialog; 